// Every engine reader threads a plain `Set<string>` for warnings (the exact
// English sentence to show) — that type is threaded through ~40 files, most
// of which only ever pass it along without adding to it themselves. Changing
// that shared type to something structured would mean touching every one of
// those files (and every test constructing a `new Set()` to pass in), for a
// P1 fix that's really only about ~40 files' worth of `warnings.add(...)`
// call sites (P1 deep-review).
//
// `warningText()`/`getWarningTranslation()` below are this engine's own
// names for `i18n/textRegistry.ts`'s shared, layer-agnostic mechanism (kept
// as thin re-exports rather than migrating every one of this engine's own
// ~50 call sites to the generic names) — `model/inp/`'s own warning-producing
// functions (`georeference.ts`, `parseInp.ts`) use that same shared registry
// directly, under their own `modelWarning.*` keys, so both families of
// warnings get the identical translated-in-the-UI treatment.
import { registerText, getTextTranslation, type TextTranslation } from "../i18n/textRegistry.ts";
import type { TextKey } from "../i18n/en.ts";

type WarningTranslation = TextTranslation;

/**
 * Builds (and registers the translation for) one engine warning's English
 * text. `key` must be `engineWarning.<code>` — every en.ts/es.ts pair for
 * one of these carries the same `{param}`-style placeholders `template`
 * already has, so `t(key, params)` renders the same sentence, translated.
 */
export function warningText(
  key: TextKey,
  template: string,
  params?: Record<string, string | number>
): string {
  return registerText(key, template, params);
}

/** `undefined` for a warning never built via `warningText` — the banner falls back to the raw English text in that case. */
export function getWarningTranslation(message: string): WarningTranslation | undefined {
  return getTextTranslation(message);
}
